import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../../../../axiosConfig";

// Existing editable components (keep as-is)
import MerchantForm from "../../../Component/Merchant/BasicInfo";
import MerchantInformation from "../../../Component/Merchant/MerchantInformation";
import Document from "../../../Component/Merchant/Document";
import BankAccountManagement from "../../../Component/Merchant/BankAccountManagement";

// View-only tabs (new inline UI in this file)
import { Trash2, ExternalLink, RefreshCw } from "lucide-react";

const EditNavigationTabs = () => {
  const [searchParams] = useSearchParams();
  const uuidFromUrl = searchParams.get("uuid");

  const [activeTab, setActiveTab] = useState("basic");

  // Basic editable states (for your MerchantForm + MerchantInformation)
  const [entityUuid, setEntityUuid] = useState(uuidFromUrl || "");
  const [entityId, setEntityId] = useState(null);
  const [entityName, setEntityName] = useState("");
  const [entityType, setEntityType] = useState("");
  const [entityCode, setEntityCode] = useState("");
  const [entityWebsite, setEntityWebsite] = useState("");
  const [entityDescription, setEntityDescription] = useState("");
  const [entityTags, setEntityTags] = useState("");
  const [entityViewData, setEntityViewData] = useState(null);

  // View-only tab data
  const [documents, setDocuments] = useState([]);
  const [localImageServer, setLocalImageServer] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [entityBranches, setEntityBranches] = useState([]);

  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);

  const pageTitle = useMemo(() => {
    return uuidFromUrl ? "Edit Merchant Information" : "Merchant Information";
  }, [uuidFromUrl]);

  const formatDateTime = (s) => {
    if (!s) return "-";
    const d = new Date(String(s).replace(" ", "T"));
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const buildDocUrl = (base, fullPath) => {
    if (!fullPath) return "";
    const b = (base || "").replace(/\/+$/, "");
    const p = String(fullPath).replace(/^\/+/, "");
    return b ? `${b}/${p}` : p;
  };

  // -------- Fetch entity view-info (for edit tabs + view-only tabs) --------
  useEffect(() => {
    const fetchEntityData = async () => {
      if (!uuidFromUrl) return;

      setIsLoadingEntity(true);
      try {
        const response = await axiosInstance.get(
          `/internal-api/v1/client-web/entity-panel/config/${encodeURIComponent(
            uuidFromUrl
          )}/view-info`
        );

        if (response.data?.status && response.data?.result?.entity) {
          const entity = response.data.result.entity;

          setEntityUuid(entity.uuid);
          setEntityId(entity.id);
          setEntityName(entity.name || "");
          setEntityType(entity.entityType || "");
          setEntityCode(entity.registration || entity.entityCode || "");
          setEntityWebsite(entity.website || "");
          setEntityDescription(entity.description || "");
          setEntityTags(entity.tags || "");
          setEntityViewData(entity || null);
          setEntityBranches(response.data.result.entityBranches || []);

          setDocuments(response.data.result.entityDocuments || []);
          setLocalImageServer(response.data.result.localImageServer || "");
        } else {
          alert("Failed to load merchant data.");
        }
      } catch (error) {
        console.error("Error fetching entity data:", error);
        alert("Failed to load merchant data. Please try again.");
      } finally {
        setIsLoadingEntity(false);
      }
    };

    fetchEntityData();
  }, [uuidFromUrl]);

  // -------- Fetch bank accounts (view-only; filter by this uuid) --------
  const fetchBankAccounts = async () => {
    if (!entityUuid) return;

    setBankLoading(true);
    try {
      const res = await axiosInstance.get(
        `/internal-api/v1/client-web/entity-panel/registration/entity-bank-accounts`,
        { params: { start: 0, length: 100 } }
      );

      const rows = res.data?.data || [];
      setBankAccounts(rows.filter((r) => r.entityUuid === entityUuid));
    } catch (e) {
      console.error("Bank accounts error:", e);
      setBankAccounts([]);
    } finally {
      setBankLoading(false);
    }
  };

  useEffect(() => {
    if (entityUuid) fetchBankAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityUuid]);

  return (
    <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-md">
      <h1 className="p-4 text-lg font-medium border-b">{pageTitle}</h1>

      {isLoadingEntity && (
        <div className="p-6 text-center">
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading merchant data...
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b">
        <TabButton label="Basic Info" active={activeTab === "basic"} onClick={() => setActiveTab("basic")} />
        <TabButton label="Branch" active={activeTab === "location"} onClick={() => setActiveTab("location")} />
        <TabButton label="Document" active={activeTab === "images"} onClick={() => setActiveTab("images")} />
        <TabButton label="Bank Details" active={activeTab === "contact"} onClick={() => setActiveTab("contact")} />
      </div>

      {/* Tab Contents */}
      {!isLoadingEntity && activeTab === "basic" && (
        <div className="p-6">
          <MerchantForm
            isEditMode={!!uuidFromUrl}
            initialEntity={entityViewData}
            entityUuid={entityUuid}
            setEntityUuid={setEntityUuid}
            entityId={entityId}
            setEntityId={setEntityId}
            entityName={entityName}
            setEntityName={setEntityName}
            entityType={entityType}
            setEntityType={setEntityType}
            entityCode={entityCode}
            setEntityCode={setEntityCode}
            entityWebsite={entityWebsite}
            setEntityWebsite={setEntityWebsite}
            entityDescription={entityDescription}
            setEntityDescription={setEntityDescription}
            entityTags={entityTags}
            setEntityTags={setEntityTags}
            setActiveTab={setActiveTab}
          />
        </div>
      )}

      {!isLoadingEntity && activeTab === "location" && (
        <div className="p-6">
          <MerchantInformation
            isEditMode={!!uuidFromUrl}
            entityUuid={entityUuid}
            entityId={entityId}
            entityName={entityName}
            entityType={entityType}
            entityCode={entityCode}
            initialBranches={entityBranches}
            setActiveTab={setActiveTab}
          />
        </div>
      )}

      {/* DOCUMENTS */}
      {!isLoadingEntity && activeTab === "images" && (
        <div className="p-6">
          <Document
            setActiveTab={setActiveTab}
            activeTab={activeTab}
            entityUuid={entityUuid}
            entityId={entityId}
            entityName={entityName}
            entityType={entityType}
            entityCode={entityCode}
          />
        </div>
      )}

      {/* BANK ACCOUNTS */}
      {!isLoadingEntity && activeTab === "contact" && (
        <div className="p-6">
          <BankAccountManagement
            isEditMode={true}
            entityUuid={entityUuid}
            entityId={entityId}
            entityName={entityName}
            entityType={entityType}
            entityCode={entityCode}
            setActiveTab={setActiveTab}
          />
        </div>
      )}
    </div>
  );
};

const TabButton = ({ label, active, onClick }) => {
  return (
    <button
      className={`px-6 py-3 font-medium text-sm whitespace-nowrap ${
        active ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-600 hover:text-slate-800"
      }`}
      onClick={onClick}
      type="button"
    >
      {label}
    </button>
  );
};

const InfoBox = ({ label, value }) => {
  return (
    <div className="p-3 bg-white border rounded">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-sm break-words text-slate-800">{value}</div>
    </div>
  );
};

export default EditNavigationTabs;
